import { type DeferredDismissState } from '@littlebird/action-cards';
import { useEffect, useRef, type ReactNode } from 'react';

export const DismissHoldBoundary = ({
  stripIds,
  controller,
  children,
}: {
  readonly stripIds: readonly string[];
  readonly controller: DeferredDismissState;
  readonly children: ReactNode;
}) => {
  const controllerRef = useRef(controller);
  controllerRef.current = controller;
  const rootRef = useRef<HTMLSpanElement>(null);
  const pointerInside = useRef(false);
  const heldIds = stripIds.join(',');

  useEffect(() => {
    if (heldIds.length === 0) return;
    const ids = heldIds.split(',');
    if (rootRef.current?.matches(':hover')) {
      for (const id of ids) controllerRef.current.pause(id);
    }
    return () => {
      for (const id of ids) controllerRef.current.resume(id);
    };
  }, [heldIds]);

  if (stripIds.length === 0) return children;

  const pauseHeld = () => {
    for (const id of stripIds) controller.pause(id);
  };
  const resumeHeld = () => {
    for (const id of stripIds) controller.resume(id);
  };

  // Span, not div: chat cards render inside markdown paragraphs.
  // block, not contents: pointerenter does not bubble, so the wrapper
  // needs a box that can be the hover target for the whole card.
  return (
    <span
      ref={rootRef}
      className="block"
      onPointerEnter={() => {
        pointerInside.current = true;
        pauseHeld();
      }}
      onPointerLeave={() => {
        pointerInside.current = false;
        if (!rootRef.current?.contains(document.activeElement)) resumeHeld();
      }}
      onFocus={pauseHeld}
      onBlur={(event) => {
        if (
          !pointerInside.current &&
          !event.currentTarget.contains(event.relatedTarget)
        )
          resumeHeld();
      }}
    >
      {children}
    </span>
  );
};
